import { useTranslation } from 'react-i18next';
import { HiMiniCheck as CheckIcon } from 'react-icons/hi2';
import {
  Button,
  Divider,
  presetNames,
  Select,
  Tabs,
  type AppWidth as AppWidthType,
  type ThemeMode
} from '@knack/asterisk-react';

import { cn } from '@/utils/tailwind';
import { BackgroundImage } from '@/pages/themes/theme-editor/BackgroundImage';
import { useThemesMessagingContext } from '@/pages/themes/theme-editor/MessagingContext';
import { Section } from '@/pages/themes/theme-editor/Section';
import { useThemesPageContext } from '@/pages/themes/ThemesPageContext';

// Keep the TW colors here, otherwise the parser won't pick them up
// and the interpolation in the button below will not work
const presetColors = [
  'bg-brand',
  'bg-blue-500',
  'bg-cyan-500',
  'bg-lime-500',
  'bg-green-500',
  'bg-pink-500',
  'bg-fuchsia-500',
  'bg-violet-500',
  'bg-amber-500'
] as const;

const fonts = [
  'Inter',
  'Fira Sans',
  'Libre Franklin',
  'Merriweather Sans',
  'Lato',
  'Nunito',
  'Nunito Sans',
  'Open Sans',
  'PT Sans',
  'Roboto',
  'Rubik',
  'Source Sans 3'
] as const;

export function General() {
  const [t] = useTranslation();
  const { theme, setThemeField } = useThemesPageContext();
  const { sendMessageToLiveApp } = useThemesMessagingContext();
  const themeModeOptions = [
    {
      children: <span>{t('themes.light_mode')}</span>,
      value: 'light'
    },
    {
      children: <span>{t('themes.dark_mode')}</span>,
      value: 'dark'
    },
    {
      children: <span>{t('themes.system')}</span>,
      value: 'system'
    }
  ];
  const appWidthOptions = [
    {
      children: <span>{t('themes.full')}</span>,
      value: 'full'
    },
    {
      children: <span>{t('themes.max')}</span>,
      value: 'max'
    }
  ];

  return (
    <>
      <Section sectionNameKey="appearance">
        <div className="my-4 flex flex-wrap gap-1.5">
          {presetNames.map((preset, i) => (
            <Button
              intent="minimal"
              key={preset}
              title={preset}
              className={cn(
                `size-6 rounded-full border border-default text-white ${presetColors[i]} hover:${presetColors[i]} active:${presetColors[i]} focus:${presetColors[i]}`
              )}
              onClick={() =>
                setThemeField((draft) => {
                  draft.colors.preset = preset;
                })
              }
            >
              {theme.colors.preset === preset && (
                <>
                  <Button.Icon icon={CheckIcon} />
                  <span className="sr-only">{t('themes.selected_preset')}</span>
                </>
              )}
            </Button>
          ))}
        </div>
        <Tabs
          defaultValue={theme.mode}
          onValueChange={(value) => {
            setThemeField((draft) => {
              draft.mode = value as ThemeMode;
            });
            setTimeout(() => {
              sendMessageToLiveApp({
                action: 'set-theme-preview-mode',
                mode: value as ThemeMode
              });
            }, 0);
          }}
        >
          <Tabs.List items={themeModeOptions} shouldDisableResponsive />
        </Tabs>
      </Section>
      <Divider className="my-4" />
      <Section sectionNameKey="background">
        <BackgroundImage />
      </Section>
      <Divider className="my-4" />
      <Section sectionNameKey="app_width">
        <Tabs
          defaultValue={theme.appWidth}
          onValueChange={(value) => {
            setThemeField((draft) => {
              draft.appWidth = value as AppWidthType;
            });
          }}
        >
          <Tabs.List items={appWidthOptions} shouldDisableResponsive />
        </Tabs>
      </Section>
      <Divider className="my-4" />
      <Section sectionNameKey="font">
        <Select
          value={theme.font}
          onValueChange={(value) => {
            setThemeField((draft) => {
              draft.font = value;
            });
          }}
        >
          <Select.Trigger placeholder={t('keywords.default')} className="w-full rounded-lg" />
          <Select.Content>
            {fonts.map((font, index) => (
              <Select.Item key={`font-${font}`} value={font}>
                {/* The Inter (Knack's default font) name should not be displayed here due to IP reasons */}
                {index === 0 ? t('keywords.default') : font}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Section>
    </>
  );
}
